import React, { useEffect, useReducer, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { changeAutoshipPlans, getAutoshipPlans } from 'api/jalapeno';
import { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import { subscriptionPacks } from 'data/subscriptionData';
import PlanCard from 'features/account/my-plan/PlanCard';
import TaxesApply from 'features/account/my-plan/TaxesApply';
import Button from 'honeydew/Button';
import { Divider } from 'honeydew/Divider';
import { trackEvent } from 'utils/analyticsUtils';
import logError from 'utils/errorUtils';
import { isPlanPreferencesValid } from 'utils/plansUtils';

import planReducer from './reducer';

const planSelectorClasses = 'border rounded-full border-2 cursor-pointer py-10 w-[140px] md:w-[150px] text-center';

function EditPlansV2Module({ account, setError, setIsEditing }) {
    const { autopilotVariant, cartMinimum, planPreferences, subID } = account ?? {};
    const queryClient = useQueryClient();

    const [subscriptionPlan, setSubscriptionPlan] = useState(planPreferences);
    const [availablePlans, setAvailablePlans] = useState({ planSizes: [], plans: [] });
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [showOrderMinError, setShowOrderMinError] = useState(false);

    const [state, dispatch] = useReducer(planReducer, subscriptionPlan);

    // autopilot variants
    const autopilotBundlesVariant = autopilotVariant === 'bundle';
    const autopilotFlexibleVariant = autopilotVariant === 'flexible';

    // flexible plans are not required to meet order min
    const validPreferences = autopilotFlexibleVariant
        ? state?.plans?.length > 0
        : isPlanPreferencesValid(availablePlans, state, cartMinimum);

    useEffect(() => {
        setSubscriptionPlan(state);

        if (availablePlans?.plans?.length > 0) {
            const invalidPreferences = (state.plans.length > 0 && !validPreferences) || state.plans.length === 0;

            // flexible plans are not required to meet order min
            if (autopilotFlexibleVariant) {
                setShowOrderMinError(false);
            } else {
                setShowOrderMinError(invalidPreferences);
            }

            setIsSubmitDisabled(invalidPreferences);
        }
    }, [state]);

    const onPlanSizeChange = (e) => {
        dispatch({ type: 'setPlanSize', payload: e.target.value });
    };

    const handleSubmitPlanChange = async (e) => {
        e.preventDefault();
        setIsSubmitDisabled(true);

        if (validPreferences) {
            setShowOrderMinError(false);

            try {
                const response = await changeAutoshipPlans(subID, subscriptionPlan);

                if (response.msg === 'Success') {
                    queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
                    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                    setIsEditing(false);
                    setIsSubmitDisabled(false);
                    window.scrollTo({ top: 0 });
                    setError('');
                    if (subscriptionPlan.planSize !== e.target.value) {
                        trackEvent('plan range edit');
                    }
                }
            } catch (err) {
                setIsSubmitDisabled(false);
                setError('Unable to update plan');
            }
        } else {
            setShowOrderMinError(true);
            setIsSubmitDisabled(false);
        }
    };

    const getAvailablePlanData = async () => {
        try {
            const response = await getAutoshipPlans();
            if (response.data) {
                // sort cold packs to be last
                // eslint-disable-next-line no-nested-ternary
                response.data.plans.sort((a, b) => (a.coldPack === b.coldPack ? 0 : a.coldPack ? 1 : -1));
                setAvailablePlans(response.data);
            }
        } catch (e) {
            logError(e);
            console.error(e);
        }
    };

    useEffect(() => {
        getAvailablePlanData();
    }, [planPreferences]);

    return (
        <div>
            <div>
                <h4 className="text-body-md font-grotesk-bold">Plan Settings</h4>
                <p className="text-body-sm mt-5">
                    Select the packs that you&apos;d like to include in your weekly order.
                    {autopilotBundlesVariant ? '' : ' Pack selections impact your plan range (seen below).'}
                </p>
                <div className="text-body-sm bg-malt-off-white mb-10 mt-20 inline-block rounded-md px-15 py-10">
                    <span className="font-grotesk-bold">Plan Range</span> ${planPreferences.minBoxRange} - $
                    {planPreferences.maxBoxRange} per week
                </div>

                <TaxesApply />

                {!autopilotFlexibleVariant && (
                    <>
                        <p className="text-body-sm mt-25 font-grotesk-bold">How many are you shopping for?</p>
                        <fieldset className="text-body-sm my-20 flex gap-20">
                            {availablePlans?.planSizes.map((plan) => (
                                <label
                                    key={plan}
                                    htmlFor={plan}
                                    className={clsx(planSelectorClasses, {
                                        'bg-brand-secondary border-kale if:border-beet':
                                            subscriptionPlan.planSize === plan,
                                    })}
                                >
                                    <p className="font-grotesk-bold">{plan}</p>
                                    <input
                                        className="sr-only"
                                        type="radio"
                                        id={plan}
                                        name="plan-size"
                                        value={plan}
                                        checked={subscriptionPlan.planSize === plan}
                                        onChange={onPlanSizeChange}
                                    />
                                </label>
                            ))}
                        </fieldset>
                    </>
                )}
            </div>

            <Divider />

            {(autopilotBundlesVariant || autopilotFlexibleVariant) && (
                <fieldset>
                    {subscriptionPacks.map((aisle) => {
                        const savedPlan = subscriptionPlan?.plans.find((plan) => plan.id === aisle.id);

                        return (
                            <React.Fragment key={aisle.name}>
                                <PlanCard
                                    dispatch={dispatch}
                                    plan={aisle}
                                    savedPlan={savedPlan}
                                    savedPlanSize={subscriptionPlan?.planSize}
                                    bundles={autopilotBundlesVariant}
                                    flexible={autopilotFlexibleVariant}
                                />
                            </React.Fragment>
                        );
                    })}
                </fieldset>
            )}

            {!autopilotBundlesVariant &&
                !autopilotFlexibleVariant &&
                subscriptionPlan?.planSize &&
                availablePlans &&
                availablePlans.plans?.length > 0 && (
                    <>
                        <h4 className="text-body-md mb-15 font-grotesk-bold">Cart Preferences</h4>

                        <fieldset>
                            {availablePlans.plans.map((availablePlan) => {
                                const savedPlan = subscriptionPlan?.plans.find((plan) => plan.id === availablePlan.id);

                                return (
                                    <React.Fragment key={availablePlan.name}>
                                        <PlanCard
                                            dispatch={dispatch}
                                            plan={availablePlan}
                                            savedPlan={savedPlan}
                                            savedPlanSize={subscriptionPlan?.planSize}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </fieldset>
                    </>
                )}

            <Divider />

            <div>
                <p className="text-body-xs mb-15">Any updates will go into effect on your next shopping window.</p>

                {showOrderMinError && (
                    <p className="mb-15 font-grotesk-bold text-error">
                        Pack selections must meet or surpass order minimum. Make additional selection(s).
                    </p>
                )}
                <div className="max-w-[350px]">
                    <Button size="md" onClick={handleSubmitPlanChange} disabled={isSubmitDisabled}>
                        Update
                    </Button>
                    <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="text-body-sm link mx-auto mt-15 block"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

EditPlansV2Module.propTypes = {
    account: PropTypes.shape({}),
    setError: PropTypes.func.isRequired,
    setIsEditing: PropTypes.func.isRequired,
};

EditPlansV2Module.defaultProps = {
    account: {},
};

export default EditPlansV2Module;
